import { useEffect, useRef, useState } from "react";

import { receiveLADSFunctionalUnits } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import { createChannelFromFunction } from "../mapper/lads-mapper";
import { formatRawValue } from "../util/format-value";
import { logger } from "../util/logger";
import {
  ladsWebSocketService,
  type UAFunctionValueMessage,
} from "../util/websocket-lads";
import type { Channel } from "../view-model";
import { useActiveRoom } from "./use-rooms";

/**
 * Parse a monitoring path into deviceId, functionalUnitId, functionId, variableName
 * Path format: "DeviceSet/{deviceId}/FunctionalUnitSet/{fuId}/FunctionSet/{functionId}/{variableName}"
 */
function parseMonitoringPath(path: string) {
  const parts = path.split("/");
  if (parts.length < 6) return null;
  return {
    deviceId: parts[1],
    functionalUnitId: parts[3],
    functionId: parts[5],
    variableName: parts[6], // e.g. "CurrentValue", "SensorValue"
  };
}

/**
 * Fetch monitoring values from the active room's monitoring paths.
 * Uses the same channel mapping as the device detail page.
 * Subscribes to WebSocket for live updates.
 */
export function useRoomMonitoring() {
  const { activeRoom } = useActiveRoom();
  const accessToken = useAccessToken();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const channelsRef = useRef<Channel[]>([]);

  const monitoring = activeRoom?.monitoring;
  const roomId = activeRoom?.id;

  // Keep ref in sync
  useEffect(() => {
    channelsRef.current = channels;
  }, [channels]);

  // Initial REST fetch
  useEffect(() => {
    if (!monitoring || monitoring.length === 0) {
      setChannels([]);
      setError(undefined);
      return;
    }

    const firstTwo = monitoring.slice(0, 2);
    setIsLoading(true);

    // Group by deviceId to avoid duplicate fetches
    const byDevice = new Map<string, typeof firstTwo>();
    for (const entry of firstTwo) {
      const parsed = parseMonitoringPath(entry.path);
      if (!parsed) continue;
      const key = parsed.deviceId;
      if (!byDevice.has(key)) byDevice.set(key, []);
      byDevice.get(key)!.push(entry);
    }

    Promise.all(
      [...byDevice.entries()].map(async ([deviceId]) => {
        try {
          return await receiveLADSFunctionalUnits(
            deviceId,
            accessToken || undefined
          );
        } catch (error_) {
          logger.error("[useRoomMonitoring] error fetching", deviceId, error_);
          return [];
        }
      })
    )
      .then((allFUs) => {
        const allFunctions = allFUs.flat().flatMap((fu: any) =>
          (fu.functionSet || []).map((fn: any) => ({
            ...fn,
            _fuBrowseName: fu.browseName,
          }))
        );

        const results = firstTwo.map((entry) => {
          const parsed = parseMonitoringPath(entry.path);
          if (!parsed) return null;

          const func = allFunctions.find(
            (f: any) =>
              f.browseName === parsed.functionId &&
              f._fuBrowseName === parsed.functionalUnitId
          );

          if (!func) return null;

          const channel = createChannelFromFunction(
            func,
            parsed.deviceId,
            parsed.functionalUnitId
          );
          return {
            ...channel,
            name: entry.name,
            _monitoringPath: entry.path,
          } as Channel & { _monitoringPath: string };
        });

        const filtered = results.filter(
          (r): r is Channel & { _monitoringPath: string } => r !== null
        );
        setChannels(filtered);
        channelsRef.current = filtered;
        setIsLoading(false);
        setError(undefined);
      })
      .catch(() => {
        setIsLoading(false);
        setError("Failed to fetch monitoring values");
      });
  }, [monitoring, accessToken]);

  // WebSocket subscription for live updates
  useEffect(() => {
    if (!roomId || !monitoring || monitoring.length === 0) return;

    ladsWebSocketService.subscribe(roomId);

    const unsubscribe = ladsWebSocketService.onValue(
      (message: UAFunctionValueMessage) => {
        const {
          browsePath,
          data,
          deviceId,
          functionName,
          unitId,
          variableType,
        } = message;
        const current = channelsRef.current;
        if (current.length === 0) return;

        // Exact tuple match against the monitoring path. The previous
        // `includes()` heuristic accepted any path that mentioned the device
        // and function name as substrings — which silently routed an
        // organized cross-device FU's value (same functionName under a
        // different unitId) into the host's channel slot.
        const matchIndex = current.findIndex((ch: any) => {
          const monPath = ch._monitoringPath;
          if (!monPath) return false;
          const parsed = parseMonitoringPath(monPath);
          if (!parsed) return false;
          return (
            parsed.deviceId === deviceId &&
            parsed.functionalUnitId === unitId &&
            parsed.functionId === functionName &&
            parsed.variableName === variableType
          );
        });

        if (matchIndex === -1) return;

        logger.debug(
          "[Monitor] consuming:",
          browsePath,
          "→ matched _monitoringPath=",
          (current[matchIndex] as any)?._monitoringPath,
          "value=",
          data?.value
        );

        const timestamp = data.serverTimestamp || data.sourceTimestamp;
        const channel = current[matchIndex]!;
        const precision = channel.valuePrecision ?? 1;
        const formattedValue = formatRawValue(data.value, precision);

        const updated = [...current];
        updated[matchIndex] = {
          ...channel,
          status: "on",
          current: {
            value: formattedValue,
            timestamp: timestamp ? new Date(timestamp) : new Date(),
          },
        };

        channelsRef.current = updated;
        setChannels(updated);
      }
    );

    return () => {
      ladsWebSocketService.unsubscribe(roomId);
      unsubscribe();
    };
  }, [roomId, monitoring]);

  return {
    channels,
    error,
    isLoading,
    hasMonitoring: (monitoring?.length ?? 0) > 0,
  };
}
